/**
 * Distribution of exam total scores, 19 bins (0–18, spec §4.5
 * `EXAM_TOTAL_POINTS`), with the G/VG thresholds drawn as reference lines
 * (spec §4.5 `THRESHOLD_G`/`THRESHOLD_VG`, imported — never hand-copied).
 */
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { THRESHOLD_G, THRESHOLD_VG } from "@shared/exam";
import { useTheme } from "@client/app/theme";
import { useLang, useT } from "@client/i18n";
import { formatNumber, formatPoints } from "@client/lib/format";

export function ExamHistogramChart({ histogram }: { histogram: readonly number[] }) {
  const t = useT();
  const { lang } = useLang();
  const { theme } = useTheme();
  const dark = theme === "dark";

  const total = histogram.reduce((sum, n) => sum + n, 0);
  if (total === 0) {
    return (
      <p className="text-sm text-ink-600 dark:text-ink-400">{t("teacher.exams.chart.empty")}</p>
    );
  }

  const barColor = dark ? "#818cf8" : "#4f46e5";
  const lineColor = dark ? "#f87171" : "#dc2626";
  const data = histogram.map((count, score) => ({ score, count }));

  return (
    <div role="img" aria-label={t("teacher.exams.chart.title")} className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 16, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={dark ? "#1e293b" : "#e2e8f0"}
            vertical={false}
          />
          <XAxis
            dataKey="score"
            type="number"
            domain={[0, 18]}
            ticks={[0, 2, 4, 6, 8, 10, 12, 14, 16, 18]}
            tick={{ fontSize: 11, fill: dark ? "#94a3b8" : "#64748b" }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fontSize: 11, fill: dark ? "#94a3b8" : "#64748b" }}
            width={28}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            formatter={(value: number) => [
              formatNumber(value, lang),
              t("teacher.exams.chart.students"),
            ]}
            labelFormatter={(score: number) => formatPoints(score, lang)}
          />
          <ReferenceLine
            x={THRESHOLD_G}
            stroke={lineColor}
            strokeDasharray="4 4"
            label={{ value: "G", position: "top", fill: lineColor, fontSize: 11 }}
          />
          <ReferenceLine
            x={THRESHOLD_VG}
            stroke={lineColor}
            strokeDasharray="4 4"
            label={{ value: "VG", position: "top", fill: lineColor, fontSize: 11 }}
          />
          {/*
           * `isAnimationActive={false}`: see `ChapterAccuracyChart.tsx` —
           * recharts' entrance animation can race `ResponsiveContainer`'s
           * sizing on mount at a narrow initial viewport and leave the bars
           * stuck at zero height with no repaint. Same fix here.
           */}
          <Bar
            dataKey="count"
            name={t("teacher.exams.chart.students")}
            fill={barColor}
            radius={[2, 2, 0, 0]}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default ExamHistogramChart;
